import { useMemo } from 'react'
import { Stack, Typography, useTheme } from '@mui/material'
import { Serie } from '@nivo/line'
import { GroupOption } from '@/types/metrics'
import {
  ByTimeMetricReport,
  MetricReportKind,
} from '@/types/workflow-metric-reports'
import {
  formatter,
  getBottomMarginForCountChart,
  getLeftMarginForCountChart,
} from '@/utils/chart'
import useTimeChartTicks from '@/hooks/useTimeChartTicks'
import LineChart from '@/components/charts/LineChart'
import { DateRange } from './ProjectMetricsDashboard'
import { DocumentMetricsCountData } from '@/types/documents'

type ByTimeMetricLineChartProps = {
  groupBy: GroupOption
  selectedDateRange: DateRange
  addedReport?: MetricReportKind<DocumentMetricsCountData>
  completedReport?: ByTimeMetricReport
  height?: number
}

export default function AddedAndCompletedLineChart({
  groupBy,
  selectedDateRange,
  height,
  addedReport,
  completedReport,
}: ByTimeMetricLineChartProps) {
  const theme = useTheme()

  const { axisBottom, includeYear, needsToRotate, showingRange } =
    useTimeChartTicks({
      from: selectedDateRange.startDate,
      to: selectedDateRange.endDate,
      dataLength: addedReport?.reports.length || 0,
      groupedBy: groupBy,
    })

  const data: Serie[] = useMemo(() => {
    const addedData = {
      id: 'Added',
      name: 'Added',
      color: theme.palette.primary.main,
      data:
        addedReport?.reports.map((report) => ({
          x: new Date(report.date).toISOString(), // since the dates come in different formats, we need to convert them to a common format
          y: report.doc_count,
        })) || [],
    }

    const completedData = {
      id: 'Completed',
      name: 'Completed',
      color: theme.palette.success.main,
      data:
        completedReport?.reports.map((report) => ({
          x: new Date(report.date).toISOString(), // since the dates come in different formats, we need to convert them to a common format
          y: report.reports.reduce((sum, r) => sum + r.formatted_result, 0),
        })) || [],
    }
    return [addedData, completedData]
  }, [
    addedReport?.reports,
    completedReport?.reports,
    theme.palette.primary.main,
    theme.palette.success.main,
  ])

  const maxYValue = useMemo(() => {
    let maxValue = 0
    data.forEach((serie) => {
      const serieMax = Math.max(...serie.data.map((point) => point.y as number))
      if (serieMax > maxValue) {
        maxValue = serieMax
      }
    })
    return maxValue
  }, [data])

  return (
    // Not using xScale type time since how many ticks we show depend on the data and custom range
    // also, it doesn't support week precision
    <LineChart
      animate={false} // Disabling animation since lines get "stuck" in "old" places when updating group or time period
      data={data}
      height={height || 250}
      dataHasColor
      pointSize={10}
      margin={{
        bottom: getBottomMarginForCountChart(
          needsToRotate,
          includeYear,
          showingRange,
        ),
        left: getLeftMarginForCountChart(maxYValue),
        right: 20,
      }}
      xFormat={(value) =>
        formatter(
          value as string,
          groupBy,
          includeYear,
          selectedDateRange.endDate,
        )
      }
      axisBottom={axisBottom}
      tooltipContent={(point, stackedPoints) => {
        return (
          <Stack sx={{ width: 'max-content', maxWidth: 300, p: 1 }}>
            {stackedPoints.map((stackedPoint) => (
              <Typography key={stackedPoint.id} variant="body2">
                <Typography
                  color={stackedPoint.color}
                  variant="inherit"
                  component="span"
                >
                  {stackedPoint.id}
                </Typography>{' '}
                - {point.data.xFormatted}: <b>{point.data.yFormatted}</b>
              </Typography>
            ))}
          </Stack>
        )
      }}
    />
  )
}
